import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import Spinner from '~/components/Common/Spinner';

import { AVAILABLE_REGIONS_LIB_REGIONS, PRODUCT_TYPES_OPTIONS } from '~/consts/agentHub';
import type { CommissionsConditionTypes, RuleCondition } from '~/consts/agentHub';

import { useCreateCommission } from '~/hooks/apis/agent/useCreateCommission';
import { useGetAgenciesById } from '~/hooks/apis/agent/useGetAgenciesById';
import { useGetUsersSummaryByIds } from '~/hooks/apis/agent/useGetUsersSummaryByIds';
import { useUpdateCommission } from '~/hooks/apis/agent/useUpdateCommission';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useUserBreadcrumbs from '~/hooks/useUserBreadcrumbs';

import { Dayjs, SYDNEY_TIMEZONE, formatDateSlashes, toDayJs } from '~/services/TimeService';

import type { Commission, CreateCommissionParams, EditCommissionParams } from '~/types/services/agentHub';

import {
  areCommissionOptionsEqual,
  buildCommissionConditionBreakdown,
  buildCommissionGroupPayload,
  normalizeApiConditionType,
} from '~/utils/agentHub';
import { arrayToObject } from '~/utils/arrayUtils';
import { mergeCommissionAdditionalInfo } from '~/utils/commission';
import { parseCommissionRuleBoundaryToSydneyDate, serializeCommissionRuleBoundaryDate } from '~/utils/commissionDates';
import { readCommissionAdditionalInfo } from '~/utils/commissionFlightLocations';
import { capitalise } from '~/utils/stringUtils';

import {
  CONDITION_OPERATORS,
  GROUP_OPERATORS,
  TEXT_TRANSFORM_NONE,
  createFormId,
  getCommissionGroupInitialState,
} from './CommissionDialog.types';
import type {
  CommissionConditionForm,
  CommissionConditionOperator,
  CommissionGroupForm,
  CommissionGroupOperator,
} from './CommissionDialog.types';
import { CommissionGroupEditor } from './CommissionGroupEditor';

interface Props {
  data?: Commission;
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
}

type InputOptionData = string | number | Record<string, unknown>;
type InputOptionFunc = (data: InputOptionData) => { value: string; label: string };

type CommissionType = 'base' | 'additional' | 'incentive' | '';
const VALID_COMMISSION_TYPES = new Set<CommissionType>(['base', 'additional', 'incentive']);

const getCommissionLimitInputName = (region: string) => `commissionLimit-${region}`;

const SHRUNK_LABEL = {
  textField: {
    InputLabelProps: { shrink: true },
  },
};

const INPUT_BORDER = {
  sx: {
    borderRadius: 0.5,
  },
};

export default function CommissionDialog({ data, onClose, isOpen, mode }: Props) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  const [commissionGroups, setCommissionGroups] = useState<ReturnType<typeof getCommissionGroupInitialState>>(
    getCommissionGroupInitialState(),
  );
  const [regions, setRegions] = useState<Array<string>>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const commissionFormRef = useRef<HTMLFormElement>(null);
  const [shouldIncludeLimit, setShouldIncludeLimit] = useState<boolean>(false);
  const [commissionTypeChoice, setCommissionTypeChoice] = useState<CommissionType>('');
  const [priorityInput, setPriorityInput] = useState<string>('');
  const [canHaveAdditionalCommissions, setCanHaveAdditionalCommissions] = useState<boolean>(true);
  const [includesTaxesAndFees, setIncludesTaxesAndFees] = useState<boolean>(true);
  const [includesMargin, setIncludesMargin] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();
  const brand = useCurrentBrand();

  const createCommissionMutation = useCreateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule created', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });
  const updateCommissionMutation = useUpdateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule updated', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });

  const groupedProductedTypes = useMemo(() => {
    return arrayToObject(PRODUCT_TYPES_OPTIONS, (productType) => productType);
  }, []);
  const getRuleTypeFunction = useCallback(
    (type: string): InputOptionFunc => {
      switch (type) {
        case 'productType':
          return (data) => {
            const key = String(data);
            return { value: key, label: groupedProductedTypes[key] ?? key };
          };
        case 'flightCarrier':
          return (data) => {
            if (data != null && typeof data === 'object' && !Array.isArray(data)) {
              const o = data as { code?: string; name?: string; airlineCode?: string; airlineName?: string };
              const code = o.code ?? o.airlineCode ?? '';
              const name = o.name ?? o.airlineName ?? code;
              return { value: code, label: name };
            }
            const code = String(data);
            return { value: code, label: code };
          };
        default:
          return (data) => {
            if (typeof data === 'object' && data != null && !Array.isArray(data)) {
              const firstPrimitive = Object.values(data).find(
                (value): value is string | number => typeof value === 'string' || typeof value === 'number',
              );
              const normalized = firstPrimitive != null ? String(firstPrimitive) : '';
              return { value: normalized, label: normalized };
            }
            const normalized = String(data);
            return { value: normalized, label: normalized };
          };
      }
    },
    [groupedProductedTypes],
  );

  const agencyIds = useMemo(() => {
    if (!data?.commissionGroups || !isOpen) {
      return [];
    }
    return data.commissionGroups.flatMap((group) =>
      group.conditions.filter((condition) => condition.type === 'agency').map((condition) => condition.value),
    );
  }, [data?.commissionGroups, isOpen]);

  const agentIds = useMemo(() => {
    if (!data?.commissionGroups || !isOpen) {
      return [];
    }
    return data.commissionGroups.flatMap((group) =>
      group.conditions.filter((condition) => condition.type === 'agent').map((condition) => condition.value),
    );
  }, [data?.commissionGroups, isOpen]);

  const { data: agenciesData, isFetching: isLoadingAgencies } = useGetAgenciesById(agencyIds);
  const { data: agentsMap, isFetching: isLoadingAgents } = useGetUsersSummaryByIds(agentIds, brand);
  const { data: creator, isLoading: isLoadingCreator } = useUserBreadcrumbs({
    userId: isEdit && data?.createdBy,
    enabled: Boolean(isEdit && data?.createdBy),
  });
  const { data: updater, isLoading: isLoadingUpdater } = useUserBreadcrumbs({
    userId: isEdit && data?.updatedBy,
    enabled: Boolean(isEdit && data?.updatedBy),
  });

  const isLoadingCommissionEditHistory = isLoadingCreator || isLoadingUpdater;
  const isLoadingConditions = isLoadingAgencies || isLoadingAgents;
  const commissionConditionBreakdown = useMemo(
    () => buildCommissionConditionBreakdown(Object.values(commissionGroups)),
    [commissionGroups],
  );

  const resetForm = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setRegions([]);
    setCommissionGroups(getCommissionGroupInitialState());
    setShouldIncludeLimit(false);
    setCommissionTypeChoice('');
    setPriorityInput('');
    setCanHaveAdditionalCommissions(true);
    setIncludesTaxesAndFees(true);
    setIncludesMargin(true);
    commissionFormRef.current?.reset();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (isCreate && !data) {
      setCommissionTypeChoice('');
      setPriorityInput('');
      setCanHaveAdditionalCommissions(true);
      setIncludesTaxesAndFees(true);
      setIncludesMargin(true);
    }
  }, [isOpen, isCreate, data]);

  useEffect(() => {
    if (!data || !isOpen) {
      return;
    }
    setRegions(data.regions);
    setCommissionTypeChoice(VALID_COMMISSION_TYPES.has(data.type as CommissionType) ? data.type : '');
    setPriorityInput(data.priority != null ? String(data.priority) : '');
    setCanHaveAdditionalCommissions(Boolean(data.canHaveAdditionalCommissions));
    setIncludesTaxesAndFees(data.includesTaxesAndFees ?? true);
    setIncludesMargin(data.includesMargin ?? true);
    if (data.startDate) {
      setStartDate(parseCommissionRuleBoundaryToSydneyDate(data.startDate));
    }

    if (data.endDate) {
      setEndDate(parseCommissionRuleBoundaryToSydneyDate(data.endDate));
    } else {
      setEndDate(null);
    }
    setShouldIncludeLimit(data.limit != null);

    const agencyLabels = new Map(agenciesData?.map((agency) => [agency.id, agency.name]) ?? []);
    const agentLabels = new Map(
      Array.from(agentsMap?.values() ?? []).map((agent: { id_member: string; full_name: string; email: string }) => [
        agent.id_member,
        `${agent.full_name} [${agent.email}]`,
      ]),
    );

    const nextGroups =
      data.commissionGroups?.map((group) => {
        const conditions: Array<CommissionConditionForm> = [];
        group.conditions.forEach((condition) => {
          const type = normalizeApiConditionType(condition.type);
          if (!type) {
            return;
          }
          const option =
            type === 'agency'
              ? { value: condition.value, label: agencyLabels.get(condition.value) ?? condition.value }
              : type === 'agent'
                ? { value: condition.value, label: agentLabels.get(condition.value) ?? condition.value }
                : getRuleTypeFunction(type)(condition.value);
          const operator = CONDITION_OPERATORS.includes(condition.operator as CommissionConditionOperator)
            ? (condition.operator as CommissionConditionOperator)
            : 'eq';

          conditions.push({
            id: condition.id,
            type,
            operator,
            value: [option],
          });
        });

        return {
          id: group.id,
          operator: GROUP_OPERATORS.includes(group.operator as CommissionGroupOperator)
            ? (group.operator as CommissionGroupOperator)
            : 'AND',
          conditions,
        };
      }) ?? [];

    setCommissionGroups(
      nextGroups.reduce((acc, group) => {
        acc[group.operator] = group;
        return acc;
      }, getCommissionGroupInitialState()),
    );
  }, [data, isOpen, getRuleTypeFunction, agenciesData, agentsMap, agencyIds.length, agentIds.length]);

  const addCondition = useCallback(
    (operator: CommissionGroupOperator, conditionId: string, newCondition: RuleCondition) => {
      setCommissionGroups((groups) => {
        const group = groups[operator];
        const condition = group.conditions.find((currentCondition) => currentCondition.id === conditionId);
        if (!condition) {
          return groups;
        }
        if (condition.type === newCondition.type && areCommissionOptionsEqual(condition.value, newCondition.value)) {
          return groups;
        }
        return {
          ...groups,
          [operator]: {
            ...group,
            conditions: group.conditions.map((currentCondition) =>
              currentCondition.id === conditionId
                ? {
                    ...currentCondition,
                    type: newCondition.type,
                    value: [...newCondition.value],
                  }
                : currentCondition,
            ),
          },
        };
      });
    },
    [],
  );

  const handleChangeCondition = useCallback(
    (groupId: CommissionGroupOperator, conditionId: string, type: CommissionsConditionTypes) => {
      setCommissionGroups((groups) => {
        const group = groups[groupId];
        const condition = group.conditions.find((currentCondition) => currentCondition.id === conditionId);
        if (!condition) {
          return groups;
        }
        if (condition.type === type && condition.value.length === 0) {
          return groups;
        }
        return {
          ...groups,
          [groupId]: {
            ...group,
            conditions: group.conditions.map((currentCondition) =>
              currentCondition.id === conditionId
                ? {
                    ...currentCondition,
                    type,
                    value: [],
                  }
                : currentCondition,
            ),
          },
        };
      });
    },
    [],
  );

  const handleConditionOperatorChange = useCallback(
    (groupOperator: CommissionGroupOperator, conditionId: string, operator: CommissionConditionOperator) => {
      setCommissionGroups((groups) => {
        const nextGroups = {
          ...groups,
          [groupOperator]: {
            ...groups[groupOperator],
            conditions: groups[groupOperator].conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, operator } : condition,
            ),
          },
        };
        return nextGroups;
      });
    },
    [],
  );

  const handleClearCondition = useCallback((operator: CommissionGroupOperator, conditionId: string) => {
    setCommissionGroups((groups) => {
      const nextGroups = {
        ...groups,
        [operator]: {
          ...groups[operator],
          conditions: groups[operator].conditions.filter((condition) => condition.id !== conditionId),
        },
      };
      return nextGroups;
    });
  }, []);

  const handleAppendCondition = useCallback((operator: CommissionGroupOperator) => {
    setCommissionGroups((groups) => {
      const nextGroups = {
        ...groups,
        [operator]: {
          ...groups[operator],
          conditions: [
            ...groups[operator].conditions,
            {
              id: createFormId(),
              type: 'productType',
              operator: 'eq',
              value: [],
            },
          ],
        },
      };
      return nextGroups;
    });
  }, []);

  const handleStartDateChange = useCallback((date: Dayjs) => {
    setStartDate(date);
  }, []);

  const handleEndDateChange = useCallback((date: Dayjs) => {
    setEndDate(date);
  }, []);

  async function handleSaveCommission(
    event: FormEvent<HTMLFormElement>,
    id: string,
    groups: Array<CommissionGroupForm>,
  ) {
    event.preventDefault();

    const commissionGroupPayload = buildCommissionGroupPayload(groups);
    const isConditionsEmpty = commissionGroupPayload.length === 0;

    if (isConditionsEmpty) {
      enqueueSnackbar('Please add at least one condition', { variant: 'error' });
      return;
    }

    const formData = new FormData(event.currentTarget);
    const regionsForm = formData?.get('regions') as string;
    const regionsValue = regionsForm === '' ? [] : regionsForm.split(',');
    const limitValue = formData.get('limit') as string;

    if (regionsValue.length === 0) {
      enqueueSnackbar('Please select at least one region', { variant: 'error' });
      return;
    }

    if (!VALID_COMMISSION_TYPES.has(commissionTypeChoice)) {
      enqueueSnackbar('Please select a commission type', { variant: 'error' });
      return;
    }

    let priority: number | null = null;
    if (commissionTypeChoice === 'base') {
      const trimmedPriority = priorityInput.trim();
      if (trimmedPriority !== '') {
        const parsed = parseInt(trimmedPriority, 10);
        if (Number.isNaN(parsed) || parsed < 0) {
          enqueueSnackbar('Priority must be a number greater or equal to 0', { variant: 'error' });
          return;
        }
        priority = parsed;
      }
    }

    const commissionValueLimits: Array<{ region: string; maxAmount: number }> = [];
    if (commissionTypeChoice === 'incentive') {
      for (const region of regionsValue) {
        const maxAmountValue = formData.get(getCommissionLimitInputName(region)) as string;
        const trimmedMaxAmountValue = maxAmountValue.trim();

        if (trimmedMaxAmountValue === '') {
          continue;
        }

        const parsedMaxAmount = Number(trimmedMaxAmountValue);
        if (Number.isNaN(parsedMaxAmount) || parsedMaxAmount < 1) {
          enqueueSnackbar('Commission value limit must be a number greater or equal to 1', { variant: 'error' });
          return;
        }

        commissionValueLimits.push({ region, maxAmount: parsedMaxAmount });
      }
    }

    const additionalInfo = mergeCommissionAdditionalInfo(
      isEdit ? readCommissionAdditionalInfo(data) : undefined,
      groups.flatMap((group) =>
        group.conditions
          .filter((condition) => condition.type === 'flightDeparture')
          .flatMap((condition) => condition.value),
      ),
      groups.flatMap((group) =>
        group.conditions
          .filter((condition) => condition.type === 'flightArrival')
          .flatMap((condition) => condition.value),
      ),
    );

    const formObj = {
      rules: {},
      commissionGroups: commissionGroupPayload,
      description: formData.get('description') as string,
      startDate: startDate
        ? serializeCommissionRuleBoundaryDate(startDate)
        : serializeCommissionRuleBoundaryDate(toDayJs()),
      endDate: endDate ? serializeCommissionRuleBoundaryDate(endDate) : undefined,
      regions: regionsValue,
      discountType: commissionTypeChoice,
      discountAmount: parseInt(formData.get('discountAmount') as string, 10),
      commissionType: commissionTypeChoice,
      commissionPercentage: parseInt(formData.get('discountAmount') as string, 10),
      type: commissionTypeChoice,
      limit: shouldIncludeLimit ? parseInt(limitValue, 10) : null,
      commissionValueLimits,
      includesTaxesAndFees,
      includesMargin,
      ...(additionalInfo ? { additionalInfo } : {}),
    };

    const payload = commissionTypeChoice === 'base' ? { ...formObj, priority, canHaveAdditionalCommissions } : formObj;

    const onSuccess = () => {
      const hasChangedLimit =
        (data?.limit && Number(limitValue) !== data?.limit) || (!data?.limit && Number(limitValue) !== 0);
      if (hasChangedLimit) {
        enqueueSnackbar('Limit updated, this may take a few minutes to take effect', { variant: 'warning' });
      }
      resetForm();
      onClose();
    };

    if (isCreate) {
      createCommissionMutation.mutate(payload as CreateCommissionParams, { onSuccess });
    } else {
      updateCommissionMutation.mutate({ commissionId: data?.id, data: payload as EditCommissionParams }, { onSuccess });
    }
  }

  function onCloseModal() {
    resetForm();
    onClose();
  }

  const isSaving = createCommissionMutation.isPending || updateCommissionMutation.isPending;

  return (
    <Dialog open={isOpen} fullWidth onClose={onCloseModal}>
      <DialogTitle>{isCreate ? 'Create commission rule' : `Edit commission rule "${data?.description}"`}</DialogTitle>
      <form
        ref={commissionFormRef}
        onSubmit={(event) => handleSaveCommission(event, data?.id, Object.values(commissionGroups))}
      >
        <Stack padding={2} paddingTop={0} direction="column" spacing={2}>
          {data?.hasReachedLimit && (
            <Alert severity="warning">
              This commission rule has reached its limit of usage and is no longer active. If you change it, it can
              accidentally become active again.
            </Alert>
          )}
          <Stack direction="row" gap={2}>
            <TextField
              placeholder="e.g. Flight Center UK - Hotel + Tours"
              name="description"
              defaultValue={data?.description}
              label="Description"
              required
              fullWidth
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
              InputProps={INPUT_BORDER}
            />
          </Stack>
          <Stack direction="row" gap={2} justifyContent="space-between">
            <FormControl fullWidth>
              <InputLabel
                sx={{ backgroundColor: 'background.paper', padding: '0 4px' }}
                shrink
                id="agent-hub-commissions-discount-type-input-label"
              >
                Commission type
              </InputLabel>
              <Select
                value={commissionTypeChoice}
                displayEmpty
                onChange={(event) => setCommissionTypeChoice(event.target.value as CommissionType)}
                renderValue={(value) => {
                  if (!value) {
                    return <Typography color="text.secondary">Select commission type</Typography>;
                  }
                  return capitalise(value);
                }}
                label="Commission type"
                required
                fullWidth
                sx={INPUT_BORDER.sx}
              >
                <MenuItem value="base">Base</MenuItem>
                <MenuItem value="additional">Additional</MenuItem>
                <MenuItem value="incentive">Incentive</MenuItem>
              </Select>
            </FormControl>
            <TextField
              name="discountAmount"
              label="Commission percentage"
              defaultValue={data?.commissionPercentage}
              type="number"
              required
              fullWidth
              placeholder="e.g. 8%"
              slotProps={{
                htmlInput: { min: 1, max: 100 },
                input: INPUT_BORDER,
                inputLabel: SHRUNK_LABEL.textField.InputLabelProps,
              }}
            />
          </Stack>

          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includesTaxesAndFees}
                  onChange={(event) => setIncludesTaxesAndFees(event.target.checked)}
                  size="small"
                />
              }
              label="Include taxes and fees"
            />
            <Tooltip title="If disabled, the LE margin/POS commission for the offer will not be counted as part of the commission, so if the total is $1000 but LE margin is $100, the commission will be applied in the remaining $900">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includesMargin}
                    onChange={(event) => setIncludesMargin(event.target.checked)}
                    size="small"
                  />
                }
                label="Include margin"
              />
            </Tooltip>
          </Stack>

          {commissionTypeChoice === 'base' && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="button" color="secondary">
                  Base commission settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These options only apply when the commission type is base.
                </Typography>
              </Box>
              <Stack spacing={1}>
                <TextField
                  value={priorityInput}
                  onChange={(event) => setPriorityInput(event.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 1 }, ...INPUT_BORDER }}
                  label="Priority"
                  type="number"
                  fullWidth
                  placeholder="e.g. 0 (highest)"
                  InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={canHaveAdditionalCommissions}
                        onChange={(event) => setCanHaveAdditionalCommissions(event.target.checked)}
                        size="small"
                      />
                    }
                    label="Can apply additional commissions"
                  />
                </Stack>
              </Stack>
            </Stack>
          )}

          <Stack direction="row" gap={2}>
            <Stack flex={1}>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                format="DD/MM/YYYY"
                name="startDate"
                label="Start date"
                timezone={SYDNEY_TIMEZONE}
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
            <Stack flex={1}>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                format="DD/MM/YYYY"
                disablePast
                name="endDate"
                label="End date"
                timezone={SYDNEY_TIMEZONE}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={2}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                sx={{ backgroundColor: 'background.paper', padding: '0 4px' }}
                shrink
                id="agent-hub-commissions-regions-input-label"
              >
                Regions
              </InputLabel>
              <Select
                displayEmpty
                name="regions"
                labelId="agent-hub-commissions-regions-input-label"
                multiple
                value={regions}
                input={<OutlinedInput label="Regions" />}
                sx={INPUT_BORDER.sx}
                renderValue={(selected: Array<string>) => {
                  if (selected.length === 0) {
                    return <Typography color="text.secondary">Select regions</Typography>;
                  }
                  return selected.join(', ');
                }}
                onChange={(event) => {
                  setRegions(event.target.value as Array<string>);
                }}
              >
                {AVAILABLE_REGIONS_LIB_REGIONS.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    <Checkbox checked={regions.includes(region.code)} />
                    <ListItemText primary={region.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack flex={1} spacing={1}>
              <TextField
                name="limit"
                InputProps={{ inputProps: { min: 0 }, ...INPUT_BORDER }}
                label="Limit (of usage)"
                defaultValue={data?.limit}
                type="number"
                fullWidth
                disabled={!shouldIncludeLimit}
                InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
                placeholder="e.g. 5"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldIncludeLimit}
                    onChange={(e) => setShouldIncludeLimit(e.target.checked)}
                    size="small"
                  />
                }
                label="Set limit to commission"
              />
            </Stack>
          </Stack>
          {commissionTypeChoice === 'incentive' && regions.length > 0 && (
            <Stack direction="row" gap={2}>
              {regions.map((region) => (
                <TextField
                  key={region}
                  name={getCommissionLimitInputName(region)}
                  label={`Max amount (${region})`}
                  defaultValue={data?.commissionValueLimits?.find((limit) => limit.region === region)?.maxAmount}
                  type="number"
                  fullWidth
                  placeholder="e.g. 100"
                  slotProps={{
                    htmlInput: { min: 1 },
                    input: INPUT_BORDER,
                    inputLabel: SHRUNK_LABEL.textField.InputLabelProps,
                  }}
                />
              ))}
            </Stack>
          )}
          <Divider />

          <Stack spacing={2}>
            <Box>
              <Typography variant="button" color="secondary">
                Conditions:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Conditions are organized into groups. Choose AND or OR for each group, then add as many conditions as
                needed inside it. If there are both AND and OR conditions, both must be satisfied for the commission
                rule to be met.
              </Typography>
            </Box>

            {isLoadingConditions && <Spinner size={14} />}
            <Stack direction="column" spacing={3}>
              {Object.values(commissionGroups).map((group) => (
                <CommissionGroupEditor
                  key={group.id}
                  group={group}
                  brand={brand}
                  addCondition={addCondition}
                  handleAppendCondition={handleAppendCondition}
                  handleChangeCondition={handleChangeCondition}
                  handleConditionOperatorChange={handleConditionOperatorChange}
                  handleClearCondition={handleClearCondition}
                />
              ))}
            </Stack>
          </Stack>

          {isLoadingCommissionEditHistory && <Spinner size={14} />}

          {creator && (
            <Stack>
              <Typography variant="caption" color="secondary">
                Created By: <Link href={`/users/${creator?.user.href}`}>{creator?.user.label}</Link> at{' '}
                {formatDateSlashes(data?.createdAt)}
              </Typography>
              {updater && (
                <Typography variant="caption" color="secondary">
                  Edited By: <Link href={`/users/${updater?.user.href}`}>{updater?.user.label}</Link> at{' '}
                  {formatDateSlashes(data?.updatedAt)}
                </Typography>
              )}
            </Stack>
          )}

          {commissionConditionBreakdown.length > 0 && (
            <Card variant="outlined" sx={{ borderRadius: 1, p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="button" color="secondary">
                  Commission condition breakdown
                </Typography>
                <Stack spacing={0.5}>
                  {commissionConditionBreakdown.map((line, index) => (
                    <Typography key={`${line}-${index}`} variant="body2">
                      {line}
                      {index < commissionConditionBreakdown.length - 1 && (
                        <Typography component="span" variant="body2" fontWeight={600}>
                          {' '}
                          AND
                        </Typography>
                      )}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </Card>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button sx={TEXT_TRANSFORM_NONE} type="button" size="medium" variant="text" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button sx={TEXT_TRANSFORM_NONE} type="submit" size="medium" variant="contained" disabled={isSaving}>
              {isSaving ? 'Saving...' : isCreate ? 'Create rule' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  );
}
